import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, UtensilsCrossed, Car, Home as HomeIcon, HeartPulse } from 'lucide-react';
import { useGDLData } from './useGDLData';
import { ProgressBar } from './GDLComponents';

const BENEFIT_TOTAL = 93000;
const BENEFITS = [
  { key: 'food', label: 'Food Allowance', Icon: UtensilsCrossed, alloc: 25000, color: '#10B981', desc: 'Groceries, meals & dining' },
  { key: 'transport', label: 'Transport', Icon: Car, alloc: 18000, color: '#3B82F6', desc: 'Rides, fuel & commute' },
  { key: 'housing', label: 'Housing Subsidy', Icon: HomeIcon, alloc: 35000, color: '#F59E0B', desc: 'Rent, utilities & maintenance' },
  { key: 'hmo', label: 'HMO Coverage', Icon: HeartPulse, alloc: 15000, color: '#EF4444', desc: 'Health insurance & medical' },
];

function CompRow({ label, value, bold, green, isDeduction }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '11px 18px',
      background: bold ? 'rgba(176,49,64,0.03)' : 'transparent',
    }}>
      <span style={{ fontSize: 14, fontWeight: bold ? 700 : 400, color: '#2D0A10' }}>{label}</span>
      <span style={{
        fontSize: 14, fontWeight: bold ? 700 : 500,
        color: green ? '#10B981' : isDeduction ? '#EF4444' : '#2D0A10',
      }}>
        {isDeduction ? '-' : green ? '+' : ''}{'\u20A6'}{Math.abs(value).toLocaleString()}
      </span>
    </div>
  );
}

function AnimatedBar({ value, maxVal, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0;
  useEffect(() => { const t = setTimeout(() => setWidth(pct), delay); return () => clearTimeout(t); }, [pct, delay]);
  return (
    <div style={{ height: 28, borderRadius: 10, background: 'rgba(176,49,64,0.06)', overflow: 'hidden', position: 'relative' }}>
      <div style={{ height: '100%', borderRadius: 10, background: color, width: `${width}%`, transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)' }} />
      <span style={{
        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
        fontSize: 12, fontWeight: 600, color: width > 50 ? '#fff' : '#2D0A10',
      }}>
        {'\u20A6'}{value.toLocaleString()}
      </span>
    </div>
  );
}

export default function GDLBenefits() {
  const nav = useNavigate();
  const { wallet, billsHistory } = useGDLData();

  if (!wallet) return <SkeletonBenefits />;

  const { civil_servant: cs } = wallet;
  const salary = cs.monthly_salary || 185000;
  const paye = Math.round(salary * 0.0616);
  const pension = Math.round(salary * 0.08);
  const netCash = salary - paye - pension;
  const totalPackage = netCash + BENEFIT_TOTAL;

  let totalSpent = 0;
  const catSpent = {};
  (billsHistory || []).forEach(b => {
    const cat = (b.category || '').toLowerCase();
    if (cat.includes('feed') || cat.includes('food')) { catSpent.food = (catSpent.food || 0) + b.amount; totalSpent += b.amount; }
    else if (cat.includes('transport')) { catSpent.transport = (catSpent.transport || 0) + b.amount; totalSpent += b.amount; }
    else if (cat.includes('hous')) { catSpent.housing = (catSpent.housing || 0) + b.amount; totalSpent += b.amount; }
    else if (cat.includes('hmo') || cat.includes('health')) { catSpent.hmo = (catSpent.hmo || 0) + b.amount; totalSpent += b.amount; }
  });
  const remaining = Math.max(BENEFIT_TOTAL - totalSpent, 0);
  const pct = (remaining / BENEFIT_TOTAL) * 100;

  return (
    <div style={{ padding: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* ── Hero Pool ───────────────────── */}
      <div style={{
        borderRadius: 24, overflow: 'hidden', position: 'relative',
        background: 'linear-gradient(145deg, #C43B4D 0%, #B03140 40%, #8E2233 100%)',
        boxShadow: '0 8px 32px rgba(176,49,64,0.3)',
      }}>
        <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
        <div style={{ padding: '24px 22px 20px', position: 'relative' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>GDL+ Benefits Pool</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#fff', marginTop: 6, letterSpacing: '-0.03em' }}>
            {'\u20A6'}{remaining.toLocaleString()}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>of {'\u20A6'}{BENEFIT_TOTAL.toLocaleString()} monthly</div>
          <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.12)', marginTop: 14 }}>
            <div style={{ height: '100%', borderRadius: 2, background: '#fff', width: `${pct}%`, transition: 'width 0.6s ease' }} />
          </div>
        </div>
      </div>

      {/* ── Flexible banner ─────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(176,49,64,0.06)', borderRadius: 12, padding: '10px 14px' }}>
        <Sparkles size={15} color="#B03140" />
        <span style={{ fontSize: 13, color: '#8E2233', fontWeight: 600 }}>Flexible pool {'\u2014'} allocate to any category</span>
      </div>

      {/* ── Category Cards ──────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {BENEFITS.map(b => {
          const spent = catSpent[b.key] || 0;
          const rem = Math.max(b.alloc - spent, 0);
          return (
            <div key={b.key} onClick={() => nav('/demo/gdl/vendors')}
              data-testid={`benefit-${b.key}`}
              style={{
                background: '#fff', borderRadius: 18, padding: '18px 16px',
                cursor: 'pointer', boxShadow: '0 2px 10px rgba(176,49,64,0.06)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: `${b.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <b.Icon size={20} color={b.color} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#2D0A10', marginTop: 8 }}>{b.label}</div>
              <div style={{ fontSize: 12, color: '#A07078', marginTop: 1 }}>{b.desc}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#2D0A10', marginTop: 6 }}>{'\u20A6'}{rem.toLocaleString()}</div>
              <div style={{ height: 4, borderRadius: 2, background: 'rgba(176,49,64,0.06)', marginTop: 6 }}>
                <div style={{ height: '100%', borderRadius: 2, background: b.color, width: `${b.alloc > 0 ? (rem / b.alloc) * 100 : 0}%`, transition: 'width 0.6s ease' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Compensation Table ──────────── */}
      <div style={{ background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 2px 12px rgba(176,49,64,0.06)' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(176,49,64,0.06)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10' }}>Compensation Breakdown</div>
        </div>
        <CompRow label="Gross Salary" value={salary} />
        <CompRow label="PAYE Tax" value={paye} isDeduction />
        <CompRow label="Employee Pension" value={pension} isDeduction />
        <CompRow label="Net Cash Pay" value={netCash} bold />
        <div style={{ height: 1, background: 'rgba(176,49,64,0.06)' }} />
        <CompRow label="Benefits Pool" value={BENEFIT_TOTAL} green />
        <CompRow label="Total Package" value={totalPackage} bold />
      </div>

      {/* ── Visual Comparison ──────────── */}
      <div style={{ background: '#fff', borderRadius: 20, padding: '18px 20px', boxShadow: '0 2px 12px rgba(176,49,64,0.06)' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10', marginBottom: 14 }}>Your Advantage</div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#A07078', marginBottom: 5, fontWeight: 500 }}>Without benefits</div>
          <AnimatedBar value={netCash} maxVal={totalPackage} color="rgba(176,49,64,0.12)" delay={100} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#A07078', marginBottom: 5, fontWeight: 500 }}>With GDL+</div>
          <AnimatedBar value={totalPackage} maxVal={totalPackage} color="#B03140" delay={300} />
        </div>
        <div style={{
          textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#B03140',
          padding: '10px', background: 'rgba(176,49,64,0.06)', borderRadius: 10, marginTop: 4,
        }}>
          GDL+ gives you {'\u20A6'}{BENEFIT_TOTAL.toLocaleString()} more per month
        </div>
      </div>
    </div>
  );
}

function SkeletonBenefits() {
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="gdl-skeleton" style={{ width: '100%', height: 120, borderRadius: 24 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {[1,2,3,4].map(i => <div key={i} className="gdl-skeleton" style={{ width: '100%', height: 130, borderRadius: 18 }} />)}
      </div>
    </div>
  );
}
